import { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

import useModalScroll from './useModalScroll';

// 포탈 + 스크롤락 + 뷰포트 동기화를 한 번에 처리하는 공통 모달 래퍼 //
export default function TotalModalWrapper(props) {
  const {
    children,
    className,
    zIndex = 1000,
    backgroundColor = 'rgba(0, 0, 0, 0.5)',
    includeViewportSync = true,
    viewportTopCssVar = '--modal-viewport-top',
    viewportHeightCssVar = '--modal-viewport-height',
    viewportTopSource = 'scrollY',
    viewportLeftCssVar = '--modal-viewport-left',
    viewportWidthCssVar = '--modal-viewport-width',
    viewportLeftSource = 'scrollX',
  } = props;

  const topLevelRef = useModalScroll({
    includeViewportSync,
    viewportTopCssVar,
    viewportHeightCssVar,
    viewportTopSource,
    viewportLeftCssVar,
    viewportWidthCssVar,
    viewportLeftSource,
  });

  const positionerRef = useRef(null);

  useEffect(() => {
    const positioner = positionerRef.current;

    if (!positioner) return;

    function adjustOverflow(mutations) {
      // positioner 자신의 style 변경으로 인한 재귀 호출 방지
      if (mutations && mutations.every(m => m.target === positioner && m.type === 'attributes')) return;

      const container = topLevelRef.current;

      if (!container) return;

      const OVERFLOW_PADDING = 150;
      const isPadded = positioner.style.paddingLeft === `${OVERFLOW_PADDING}px`;
      const currentPaddingLeft = isPadded ? OVERFLOW_PADDING : 0;

      const containerLeft = container.getBoundingClientRect().left;
      const containerWidth = container.clientWidth;
      const containerRight = containerLeft + containerWidth;
      const scrollLeft = container.scrollLeft;

      let hasOverflow = false;

      positioner.querySelectorAll('*').forEach(el => {
        if (hasOverflow) return;

        if (window.getComputedStyle(el).position !== 'absolute') return;

        const rect = el.getBoundingClientRect();
        
        if (rect.width === 0 && rect.height === 0) return; // display:none 등 숨겨진 요소 무시

        // 컨테이너 뷰포트 범위 밖에 완전히 있는 요소 제외
        // (비활성 탭 패널 등 position:absolute로 의도적으로 화면 밖에 숨긴 요소)
        if (rect.left >= containerRight || rect.right <= containerLeft) return;

        const right = rect.right - containerLeft + scrollLeft - currentPaddingLeft;
        const left  = rect.left  - containerLeft + scrollLeft - currentPaddingLeft;

        if (right > containerWidth || left < 0) hasOverflow = true;
      });

      if (hasOverflow && !isPadded) positioner.style.padding = `${OVERFLOW_PADDING}px`;

      if (!hasOverflow && isPadded) positioner.style.padding = '';
    }

    const observer = new MutationObserver(adjustOverflow);

    observer.observe(positioner, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => observer.disconnect();
  }, []);

  return ReactDOM.createPortal(
    <TopLevelWrapper
      ref={topLevelRef}
      className={className}
      zIndex={zIndex}
      backgroundColor={backgroundColor}
      viewportTopCssVar={viewportTopCssVar}
      viewportHeightCssVar={viewportHeightCssVar}
      viewportLeftCssVar={viewportLeftCssVar}
      viewportWidthCssVar={viewportWidthCssVar}>
      <ModalPositioner ref={positionerRef} viewportHeightCssVar={viewportHeightCssVar}>
        <ModalCenter>
          {children}
        </ModalCenter>
      </ModalPositioner>
    </TopLevelWrapper>,
    document.body
  );
}

const startAnimation = keyframes`
  0% { opacity: 0; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const TopLevelWrapper = styled.div`
  position: absolute;

  top: var(${p => p.viewportTopCssVar}, 0px);
  left: var(${p => p.viewportLeftCssVar}, 0px);

  padding: 24px;

  width: var(${p => p.viewportWidthCssVar}, 100vw);
  max-width: var(${p => p.viewportWidthCssVar}, 100vw);
  height: var(${p => p.viewportHeightCssVar}, 100vh);

  z-index: ${p => p.zIndex};
  background-color: ${p => p.backgroundColor};

  overflow-y: auto;
  overflow-x: auto;

  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  box-sizing: border-box;

  animation-name: ${startAnimation};
  animation-duration: 0.5s;
  animation-timing-function: ease-out;

  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }
`;

const ModalPositioner = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: max-content;
  min-width: 100%;
  min-height: calc(var(${p => p.viewportHeightCssVar}, 100vh) - 48px);

  box-sizing: content-box;
`;

const ModalCenter = styled.div`
  margin: 0 auto;

  width: max-content;
  max-width: 100%;
`;
